/**
 * ReviewTenantsModalPanel
 * Panel-layer wrapper for ReviewTenantsModal.
 * Receives params: { unitId } from the WorkflowPanelProvider modal registry.
 *
 * We need suggestions to render the ReviewTenantsModal. Fetch the move-in projection
 * on open to get the current suggestions, then pass them down.
 */

import { useState, useEffect } from 'react';
import { ReviewTenantsModal } from '../../move-in/ReviewTenantsModal';
import { Skeleton } from '../../ui/skeleton';
import { getMoveIn } from '../../../lib/move-in.api';
import type { MoveInSuggestion } from '../../../types/move-in';

interface ReviewTenantsModalPanelProps {
  params: Record<string, string>;
  onClose: () => void;
}

export function ReviewTenantsModalPanel({ params, onClose }: ReviewTenantsModalPanelProps) {
  const { unitId } = params;
  const [suggestions, setSuggestions] = useState<MoveInSuggestion[]>([]);
  const [isFetching, setIsFetching] = useState(true);

  useEffect(() => {
    if (!unitId) return;
    setIsFetching(true);
    getMoveIn(unitId)
      .then((proj) => setSuggestions(proj.suggestions))
      .catch(() => setSuggestions([]))
      .finally(() => setIsFetching(false));
  }, [unitId]);

  if (!unitId) return null;

  // Show a brief loading skeleton while the projection is being fetched.
  // Returning null here would give the user no feedback that something is happening.
  if (isFetching) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        aria-label="Loading tenant review"
        role="status"
      >
        <div className="bg-background rounded-xl p-6 flex flex-col gap-3 min-w-[280px] shadow-xl">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    );
  }

  return (
    <ReviewTenantsModal
      isOpen={true}
      onClose={onClose}
      unitId={unitId}
      suggestions={suggestions}
      onTenantsAdded={onClose}
    />
  );
}
